import { biText } from '@euler/app/components/message/widgets/bi-visualization/functions/biText';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDatasetCatalog } from '@euler/model/bi/catalog';
import {
  BiAggregation,
  BiMeasureKind,
  BiMeasureRef,
} from '@euler/model/bi/query';
import { BiFieldRole } from '@euler/model/bi/result';
import { memo } from 'react';
import { View } from 'react-native';
import { BiChip } from '../../components/ui/BiChip';
import { EditorSection } from './EditorSection';
import { biFieldLabel, kBiAggregationLabels } from './functions/biEditorLabels';
import { BiEditorQuery } from './functions/biEditorState';

const isField = (ref: BiMeasureRef, field: string) =>
  ref.kind === BiMeasureKind.Field && ref.field === field;

/** What the card counts or sums; a measure may pick its aggregation. */
export const MeasuresSection = memo(
  ({
    query,
    catalog,
    onChange,
  }: {
    query: BiEditorQuery;
    catalog: BiDatasetCatalog | null;
    onChange: (next: BiEditorQuery) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const fields =
      catalog?.fields.filter(field => field.role === BiFieldRole.Measure) ?? [];
    const listed = new Set(fields.map(field => field.key));
    // Measures the catalog does not list (ratios, or no catalog) stay as
    // they are and can only be removed.
    const others = query.measures.filter(
      ref => ref.kind === BiMeasureKind.Ratio || !listed.has(ref.field),
    );
    const toggle = (field: string) =>
      onChange({
        ...query,
        measures: query.measures.some(ref => isField(ref, field))
          ? query.measures.filter(ref => !isField(ref, field))
          : [...query.measures, { kind: BiMeasureKind.Field, field }],
      });
    const setAgg = (field: string, agg: BiAggregation | undefined) =>
      onChange({
        ...query,
        measures: query.measures.map(ref =>
          isField(ref, field)
            ? agg
              ? { kind: BiMeasureKind.Field, field, agg }
              : { kind: BiMeasureKind.Field, field }
            : ref,
        ),
      });
    return (
      <EditorSection title="指标">
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(6) }}>
          {fields.map(field => (
            <BiChip
              key={field.key}
              label={biText(field.label)}
              selected={query.measures.some(ref => isField(ref, field.key))}
              onPress={() => toggle(field.key)}
            />
          ))}
          {others.map(ref => {
            const key = ref.kind === BiMeasureKind.Ratio ? ref.key : ref.field;
            return (
              <BiChip
                key={key}
                label={`${biFieldLabel(catalog, key)} ×`}
                selected
                onPress={() =>
                  onChange({
                    ...query,
                    measures: query.measures.filter(entry => entry !== ref),
                  })
                }
              />
            );
          })}
        </View>
        {query.measures.map(ref => {
          if (ref.kind !== BiMeasureKind.Field) return null;
          const field = fields.find(entry => entry.key === ref.field);
          if (!field?.aggs || field.aggs.length < 2) return null;
          return (
            <View
              key={ref.field}
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: rpx(6),
              }}
            >
              <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
                {biText(field.label)}
              </Typography.Text>
              {field.aggs.map(agg => (
                <BiChip
                  key={agg}
                  label={kBiAggregationLabels[agg]}
                  selected={(ref.agg ?? field.defaultAgg) === agg}
                  onPress={() =>
                    setAgg(
                      ref.field,
                      agg === field.defaultAgg ? undefined : agg,
                    )
                  }
                />
              ))}
            </View>
          );
        })}
      </EditorSection>
    );
  },
);
